/**
 * Onboarding gate — routes an OWNER/ADMIN of a not-yet-set-up workspace into
 * the kind-first wizard. Extracted from App.tsx for the 500-line ratchet.
 *
 * TRACK-B (owner-approved 2026-07-09): onboarding is re-enabled. New
 * workspaces get the wizard; the wizard (and its "Skip setup") sets
 * onboardingCompleted, so this never loops for roles that can write it.
 *
 * Role gating: setup belongs to the person who created the workspace — the
 * OWNER. Anyone invited into it (ADMIN, EDITOR, VIEWER) joins a business that
 * already exists and goes straight in, never through the wizard (owner
 * decision, launch day 2026-10-11). Before that an invited ADMIN was sent to
 * the wizard whenever the owner had not finished it; an EDITOR/VIEWER could
 * not even leave it (PUT /settings/brand → 403, QA Aug-02). Unknown role
 * fails open — never trap a user in a wizard they cannot leave.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';

export function useOnboardingGate(isPublic: boolean): boolean {
  const location = useLocation();
  const navigate = useNavigate();
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  // Membership role for the active workspace, cached so the gate doesn't
  // re-fetch /auth/me on every navigation.
  const workspaceRoleRef = useRef<{ workspaceId: string; role: string | null } | null>(null);
  // The page on which a Shopify hand-off was seen. The marketplace strips
  // `connect=shopify` from the URL as it opens the modal, and this effect
  // re-runs on that query change — without this memory the gate saw a plain
  // Integrations page and bounced the merchant to the wizard while the modal
  // was opening (browser check on a fresh account, 2026-09-25). Forgotten as
  // soon as the merchant leaves that page.
  const handoffPathRef = useRef<string | null>(null);
  // Onboarding completion is a one-way latch: a workspace that has finished
  // the wizard cannot un-finish it. Re-fetching /settings/brand on every
  // navigation buys nothing and spends one request per hop of the same
  // 100 req/min budget that the auth check needs (#589). Latch per workspace.
  const onboardingDoneRef = useRef<string | null>(null);

  useEffect(() => {
    const checkOnboarding = async (): Promise<void> => {
      // Skip check for public routes
      if (isPublic) {
        setOnboardingChecked(true);
        return;
      }

      // Skip if no workspace or auth token
      const workspaceId = api.getWorkspaceId();
      const authToken = api.getAuthToken();

      if (!workspaceId || !authToken) {
        setOnboardingChecked(true);
        return;
      }

      if (onboardingDoneRef.current === workspaceId) {
        setOnboardingChecked(true);
        return;
      }

      try {
        // Race the brand check against a 3-second timeout to prevent app from
        // being blocked by slow API responses (Render cold start, etc.)
        const brandPromise = api.settings.getBrand();
        const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000));
        const response = await Promise.race([brandPromise, timeoutPromise]);

        if (response && response.data) {
          const brandSettings = response.data;
          if (brandSettings.onboardingCompleted) {
            onboardingDoneRef.current = workspaceId;
          } else {
            let role = workspaceRoleRef.current?.workspaceId === workspaceId
              ? workspaceRoleRef.current.role
              : null;
            if (role === null) {
              try {
                const me = await api.auth.me();
                role = me.data?.workspaces?.find((w) => w.id === workspaceId)?.role ?? null;
              } catch {
                role = null;
              }
              workspaceRoleRef.current = { workspaceId, role };
            }
            // A Shopify hand-off in flight (the onboarding tile opened the
            // Connect modal on Integrations, or Shopify just sent the
            // merchant back with ?shopify=…) is part of onboarding, not a
            // detour from it. Bouncing here closed the modal before the
            // merchant could click Install.
            if (/(?:^|[?&])(?:connect=shopify|shopify=)/.test(location.search)) {
              handoffPathRef.current = location.pathname;
            } else if (handoffPathRef.current !== location.pathname) {
              handoffPathRef.current = null;
            }
            const shopifyHandoff = handoffPathRef.current === location.pathname;
            if (role === 'OWNER' && !shopifyHandoff) {
              // Keep the page the owner asked for: a deep link into
              // Integrations (docs, an email, support) used to die on the
              // wizard's landing page — the wizard now returns there after
              // Skip setup / finish (browser check on a fresh account,
              // 2026-09-26). The default landing needs no memory.
              const target = `${location.pathname}${location.search}`;
              const remember = target !== '/' && target !== '/business';
              navigate(remember ? `/onboarding?next=${encodeURIComponent(target)}` : '/onboarding');
            }
          }
        }
        // If timeout won, skip onboarding check silently
      } catch {
        // Silently fail - user can continue without onboarding check
      } finally {
        setOnboardingChecked(true);
      }
    };

    checkOnboarding();
  }, [location.pathname, location.search, isPublic, navigate]);

  return onboardingChecked;
}
